// ===== CONSTANTES DE EVENTOS FINANCEIROS =====

export const FINANCIAL_EVENTS = {
  // Eventos de transações
  TRANSACTION_CREATED: 'financial.transaction.created',
  TRANSACTION_UPDATED: 'financial.transaction.updated',
  TRANSACTION_CANCELLED: 'financial.transaction.cancelled',
  TRANSACTION_CATEGORIZED: 'financial.transaction.categorized',
  
  // Eventos de pagamentos
  PAYMENT_RECEIVED: 'financial.payment.received',
  PAYMENT_FAILED: 'financial.payment.failed',
  PAYMENT_REFUNDED: 'financial.payment.refunded',
  
  // Eventos de agendamentos
  APPOINTMENT_CREATED: 'appointment.created',
  APPOINTMENT_COMPLETED: 'appointment.completed',
  APPOINTMENT_CANCELLED: 'appointment.cancelled',
  
  // Eventos de farmácia
  PHARMACY_SALE_COMPLETED: 'pharmacy.sale.completed',

  // Injetáveis
  INJECTABLE_SESSION_COMPLETED: 'injectable.session.completed',
  
  // Eventos de estoque
  INVENTORY_PURCHASE_COMPLETED: 'inventory.purchase.completed',
  INVENTORY_SALE_COMPLETED: 'inventory.sale.completed',
  
  // Eventos de laboratório
  LAB_EXAM_COMPLETED: 'lab.exam.completed',
  
  // Eventos de pacotes
  PACKAGE_CREATED: 'package.created',
  
  // Eventos de Pauta Eletrônica
  THERAPEUTIC_ATTENDANCE_CLOSED: 'therapeutic-attendance.closed',
  
  // Eventos do Stripe
  STRIPE_PAYMENT_SUCCEEDED: 'stripe.payment.succeeded',
  STRIPE_PAYMENT_FAILED: 'stripe.payment.failed',
  STRIPE_SUBSCRIPTION_UPDATED: 'stripe.subscription.updated',
  STRIPE_SUBSCRIPTION_CANCELED: 'stripe.subscription.canceled',
  
  // Eventos de conciliação
  RECONCILIATION_COMPLETED: 'financial.reconciliation.completed',
  RECONCILIATION_MATCH_CREATED: 'reconciliation.match.created',
  RECONCILIATION_MATCH_APPROVED: 'reconciliation.match.approved',
  
  // Eventos de comissões
  COMMISSION_CREATED: 'financial.commission.created',
  COMMISSION_CALCULATED: 'financial.commission.calculated',
  
  // Eventos de notas fiscais
  INVOICE_ISSUED: 'financial.invoice.issued',
  INVOICE_CANCELLED: 'financial.invoice.cancelled',
  
  // Eventos de alertas
  ALERT_CREATED: 'financial.alert.created',
  NOTIFICATION_SENT: 'financial.notification.sent',
  
  // Eventos bancários
  BANK_SYNC_COMPLETED: 'bank.sync.completed',
  BANK_IMPORT_COMPLETED: 'bank.import.completed',
  BANK_RECONCILIATION_COMPLETED: 'bank.reconciliation.completed'
} as const;

// ===== INTERFACES BÁSICAS =====

export interface FinancialEvent {
  id: string;
  type: string;
  clinicId: string;
  userId: string;
  timestamp: Date;
  correlationId: string;
  version: number | string;
  data: any;
  metadata?: Record<string, any>;
}

export interface EventHandler<T = any> {
  canHandle(eventType: string): boolean;
  handle(event: T): Promise<void>;
}

export interface EventListener {
  id: string;
  eventType: string;
  priority: number;
  handler: EventHandler;
  isActive: boolean;
}

export interface EventSubscription {
  id: string;
  eventType: string;
  handler: EventHandler;
  config: EventConfig;
  isActive: boolean;
  createdAt: Date;
  errorCount: number;
  lastExecuted?: Date;
}

export interface EventConfig {
    maxRetries: number;
  retryDelay: number;
  deadLetterQueueEnabled: boolean;
  batchProcessing: boolean;
  batchSize: number;
  batchTimeout: number;
  enableMetrics: boolean;
  enableAudit: boolean;
}

// Funções auxiliares
export function createEventId(): string {
  return `evt_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

export function createCorrelationId(): string {
  return `corr_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

export function getEventConfig(eventType: string): EventConfig {
  return {
    maxRetries: 3,
    retryDelay: 1000,
    deadLetterQueueEnabled: true,
    batchProcessing: false,
    batchSize: 10,
    batchTimeout: 5000,
    enableMetrics: true,
    enableAudit: true
  };
}

export function isFinancialEvent(event: any): event is FinancialEvent {
  return event && 
         typeof event.id === 'string' &&
         typeof event.type === 'string' &&
         typeof event.clinicId === 'string' &&
         typeof event.userId === 'string' &&
         event.timestamp instanceof Date &&
         typeof event.correlationId === 'string' &&
         (typeof event.version === 'number' || typeof event.version === 'string') &&
         typeof event.data === 'object';
}

// ===== TIPOS DE EVENTOS ESPECÍFICOS =====

export interface AppointmentCompletedEvent extends FinancialEvent {
  type: 'appointment.completed';
  data: {
    appointmentId: string;
    patientId: string;
    patientName?: string;
    professionalId: string;
    amount: number;
    appointmentType: string;
    specialty: string;
    date: Date;
    paymentStatus: string;
    paymentMethod?: string;
    notes?: string;
    diagnosis?: string;
    prescription?: string;
    clinicId: string;
    metadata?: Record<string, any>;
  };
}

export interface PaymentReceivedEvent extends FinancialEvent {
  type: 'financial.payment.received';
  data: {
    transactionId: string;
    amount: number;
    paymentMethod: string;
    paymentDate: Date;
    reference: string;
  };
}

// ===== INTERFACES DO EVENT BUS =====

export interface EventBusConfig {
  maxRetries: number;
  retryDelay: number;
  deadLetterQueueEnabled: boolean;
  batchProcessing: boolean;
  batchSize: number;
  batchTimeout: number;
  enableMetrics: boolean;
  enableAudit: boolean;
}

export interface EventMetrics {
  totalEvents: number;
  successfulEvents: number;
  failedEvents: number;
  retryEvents: number;
  deadLetterEvents: number;
  averageProcessingTime: number;
  eventsByType: Record<string, number>;
  lastProcessedAt?: Date;
}

export interface DeadLetterEvent {
  id: string;
  originalEvent: FinancialEvent;
  failureReason: string;
  retryCount: number;
  lastAttempt: Date;
  createdAt: Date;
}

// ===== EVENT BUS IMPLEMENTATION =====

class FinancialEventBus {
  private subscriptions: Map<string, EventSubscription> = new Map();
  private eventQueue: FinancialEvent[] = [];
  private deadLetterQueue: DeadLetterEvent[] = [];
  private metrics: EventMetrics;
  private config: EventBusConfig;
  private isProcessing: boolean = false;
  private processingInterval?: NodeJS.Timeout;

  constructor(config?: Partial<EventBusConfig>) {
    this.config = {
      maxRetries: 3,
      retryDelay: 1000,
      deadLetterQueueEnabled: true,
      batchProcessing: true,
      batchSize: 10,
      batchTimeout: 5000,
      enableMetrics: true,
      enableAudit: true,
      ...config
    };

    this.metrics = {
    totalEvents: 0,
      successfulEvents: 0,
    failedEvents: 0,
      retryEvents: 0,
      deadLetterEvents: 0,
    averageProcessingTime: 0,
      eventsByType: {},
      lastProcessedAt: undefined
    };

    this.startProcessing();
  }

  // ===== SUBSCRIÇÃO DE EVENTOS =====

  registerListener(listener: EventListener): string {
    const subscriptionId = this.subscribe(listener.eventType, listener.handler, {
      maxRetries: listener.priority,
      retryDelay: 1000,
      deadLetterQueueEnabled: true,
      batchProcessing: false,
      batchSize: 1,
      batchTimeout: 0,
      enableMetrics: true,
      enableAudit: true
    });
    
    return subscriptionId;
  }

  subscribe<T extends FinancialEvent>(
    eventType: string,
    handler: EventHandler<T>,
    config?: Partial<EventConfig>
  ): string {
    const subscriptionId = `sub_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
    const eventConfig = { ...getEventConfig(eventType), ...config };

    const subscription: EventSubscription = {
      id: subscriptionId,
      eventType,
      handler: handler as EventHandler,
      config: eventConfig,
      isActive: true,
      createdAt: new Date(),
      errorCount: 0
    };

    this.subscriptions.set(subscriptionId, subscription);
    
    console.log(`📡 [EVENT-BUS] Subscrição criada: ${eventType} (${subscriptionId})`);
    
    return subscriptionId;
  }

  unsubscribe(subscriptionId: string): boolean {
    const subscription = this.subscriptions.get(subscriptionId);
    if (subscription) {
      subscription.isActive = false;
      this.subscriptions.delete(subscriptionId);
      console.log(`📡 [EVENT-BUS] Subscrição removida: ${subscriptionId}`);
      return true;
    }
    return false;
  }

  // ===== PUBLICAÇÃO DE EVENTOS =====

  async publish<T extends FinancialEvent>(event: Omit<T, 'id' | 'timestamp' | 'correlationId'>): Promise<void> {
    const eventId = createEventId();
    const correlationId = createCorrelationId();
    
    const fullEvent: T = {
      ...event,
      id: eventId,
      timestamp: new Date(),
      correlationId,
      version: '1.0'
    } as unknown as T;

    if (!isFinancialEvent(fullEvent)) {
      throw new Error(`Evento inválido: ${JSON.stringify(fullEvent)}`);
    }

    // Adicionar à fila
    this.eventQueue.push(fullEvent);
    
    // Atualizar métricas
    if (this.config.enableMetrics) {
      this.updateMetrics(fullEvent);
    }

    // Log de auditoria
    if (this.config.enableAudit) {
      console.log(`📤 [EVENT-BUS] Evento publicado: ${fullEvent.type} (${eventId})`);
    }

    // Evento adicionado à fila para processamento
  }

  // ===== PROCESSAMENTO DE EVENTOS =====

  private startProcessing(): void {
    if (this.isProcessing) return;
    
    this.isProcessing = true;
    
    if (this.config.batchProcessing) {
      this.processingInterval = setInterval(() => {
        this.processBatch();
      }, this.config.batchTimeout);
    } else {
      this.processingInterval = setInterval(() => {
        this.processNextEvent();
      }, 100);
    }
    
    console.log('🔄 [EVENT-BUS] Processamento iniciado');
  }

  private stopProcessing(): void {
    if (this.processingInterval) {
      clearInterval(this.processingInterval);
      this.processingInterval = undefined;
    }
    this.isProcessing = false;
    console.log('⏹️ [EVENT-BUS] Processamento parado');
  }

  private async processBatch(): Promise<void> {
    if (this.eventQueue.length === 0) return;

    const batch = this.eventQueue.splice(0, this.config.batchSize);
    const promises = batch.map(event => this.processEvent(event));
    
    try {
      await Promise.allSettled(promises);
    } catch (error) {
      console.error('❌ [EVENT-BUS] Erro no processamento em lote:', error);
    }
  }

  private async processNextEvent(): Promise<void> {
    const event = this.eventQueue.shift();
    if (event) {
      await this.processEvent(event);
    }
  }

  private async processEvent(event: FinancialEvent): Promise<void> {
    const startTime = Date.now();
    
    try {
      // Encontrar handlers para o tipo de evento
      const handlers = this.getHandlersForEvent(event.type);
      
      if (handlers.length === 0) {
        console.warn(`⚠️ [EVENT-BUS] Nenhum handler encontrado para: ${event.type}`);
        return;
      }

      // Processar com todos os handlers
      const promises = handlers.map(subscription => 
        this.executeHandler(subscription, event)
      );
      
      await Promise.allSettled(promises);
      
      // Atualizar métricas de sucesso
      if (this.config.enableMetrics) {
        this.metrics.successfulEvents++;
        this.metrics.lastProcessedAt = new Date();
        
        const processingTime = Date.now() - startTime;
        this.metrics.averageProcessingTime = 
          (this.metrics.averageProcessingTime + processingTime) / 2;
      }

    } catch (error) {
      console.error(`❌ [EVENT-BUS] Erro ao processar evento ${event.type}:`, error);
      
      if (this.config.enableMetrics) {
        this.metrics.failedEvents++;
      }
      
      // Adicionar à dead letter queue se configurado
      if (this.config.deadLetterQueueEnabled) {
        await this.addToDeadLetterQueue(event, error as Error);
      }
    }
  }

  private getHandlersForEvent(eventType: string): EventSubscription[] {
    return Array.from(this.subscriptions.values())
      .filter(subscription =>
        subscription.isActive &&
        subscription.eventType === eventType &&
        subscription.handler.canHandle(eventType)
      );
  }

  /** Erros de validação / negócio — retentar só multiplica o log (ex.: amount = 0). */
  private isNonRetryableHandlerError(error: any): boolean {
    const msg = (error?.message ?? String(error ?? '')).toLowerCase();
    return (
      msg.includes('amount deve ser maior que zero') ||
      msg.includes('já existe') ||
      msg.includes('already exists') ||
      msg.includes('document already exists') ||
      error?.code === 'already-exists' ||
      msg.includes('obrigatório') ||
      msg.includes('inválid')
    );
  }

  private async executeHandler(subscription: EventSubscription, event: FinancialEvent): Promise<void> {
    let retryCount = 0;
    const maxRetries = subscription.config.maxRetries;
    
    while (retryCount <= maxRetries) {
      try {
        await subscription.handler.handle(event);
        
        // Reset error count em caso de sucesso
        subscription.errorCount = 0;
        subscription.lastExecuted = new Date();
        
        return;
        
      } catch (error: any) {
        const msg = error?.message ?? String(error ?? '');
        if (this.isNonRetryableHandlerError(error)) {
          // already-exists: sucesso idempotente; validação: falha definitiva sem retry
          const isAlreadyExists =
            error?.code === 'already-exists' ||
            msg.includes('already exists') ||
            msg.includes('Document already exists') ||
            msg.includes('já existe');
          if (isAlreadyExists) {
            subscription.errorCount = 0;
            subscription.lastExecuted = new Date();
            return;
          }
          console.warn(
            `⚠️ [EVENT-BUS] Handler ${subscription.id} falhou sem retry (${msg}):`,
            error
          );
          throw error;
        }
        
        retryCount++;
        subscription.errorCount++;
        
        console.error(`❌ [EVENT-BUS] Erro no handler ${subscription.id} (tentativa ${retryCount}):`, error);
        
        if (retryCount <= maxRetries) {
          // Aguardar antes da próxima tentativa
          await this.delay(subscription.config.retryDelay * retryCount);
          
          if (this.config.enableMetrics) {
            this.metrics.retryEvents++;
          }
        } else {
          const msgLate = error?.message ?? String(error ?? '');
          if (msgLate.includes('already exists') || msgLate.includes('Document already exists') || msgLate.includes('já existe') || error?.code === 'already-exists') {
            subscription.errorCount = 0;
            return;
          }
          throw error;
        }
      }
    }
  }

  // ===== DEAD LETTER QUEUE =====

  private async addToDeadLetterQueue(event: FinancialEvent, error: Error): Promise<void> {
    const deadLetterEvent: DeadLetterEvent = {
      id: createEventId(),
      originalEvent: event,
      failureReason: error.message,
      retryCount: 0,
      lastAttempt: new Date(),
      createdAt: new Date()
    };

    this.deadLetterQueue.push(deadLetterEvent);
    
    if (this.config.enableMetrics) {
      this.metrics.deadLetterEvents++;
    }
    
    console.error(`💀 [EVENT-BUS] Evento movido para dead letter queue: ${event.type}`);
  }

  async retryDeadLetterEvent(deadLetterId: string): Promise<boolean> {
    const deadLetterIndex = this.deadLetterQueue.findIndex(dle => dle.id === deadLetterId);
    
    if (deadLetterIndex === -1) {
      return false;
    }

    const deadLetterEvent = this.deadLetterQueue[deadLetterIndex];
    deadLetterEvent.retryCount++;
    deadLetterEvent.lastAttempt = new Date();

    try {
      // Re-adicionar à fila principal
      this.eventQueue.push(deadLetterEvent.originalEvent);
      
      // Remover da dead letter queue
      this.deadLetterQueue.splice(deadLetterIndex, 1);
      
      console.log(`🔄 [EVENT-BUS] Evento reenviado da dead letter queue: ${deadLetterEvent.originalEvent.type}`);
      
      return true;
    } catch (error) {
      console.error(`❌ [EVENT-BUS] Erro ao reenviar evento da dead letter queue:`, error);
      return false;
    }
  }

  // ===== MÉTRICAS E MONITORAMENTO =====

  getMetrics(): EventMetrics {
    return { ...this.metrics };
  }

  getDeadLetterQueue(): DeadLetterEvent[] {
    return [...this.deadLetterQueue];
  }

  getQueueSize(): number {
    return this.eventQueue.length;
  }

  getActiveSubscriptions(): EventSubscription[] {
    return Array.from(this.subscriptions.values()).filter(sub => sub.isActive);
  }

  private updateMetrics(event: FinancialEvent): void {
    this.metrics.totalEvents++;
    this.metrics.eventsByType[event.type] = (this.metrics.eventsByType[event.type] || 0) + 1;
  }

  // ===== UTILITÁRIOS =====

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  // ===== LIMPEZA =====

  destroy(): void {
    this.stopProcessing();
    this.subscriptions.clear();
    this.eventQueue = [];
    this.deadLetterQueue = [];
    
    console.log('🧹 [EVENT-BUS] Event bus destruído');
  }

  // ===== MÉTODOS DE DEBUG =====

  async publishSync<T extends FinancialEvent>(event: Omit<T, 'id' | 'timestamp' | 'correlationId'>): Promise<void> {
    const eventId = createEventId();
    const correlationId = createCorrelationId();
    
    const fullEvent: T = {
      ...event,
      id: eventId,
      timestamp: new Date(),
      correlationId,
      version: '1.0'
    } as unknown as T;

    if (!isFinancialEvent(fullEvent)) {
      throw new Error(`Evento inválido: ${JSON.stringify(fullEvent)}`);
    }

    // Processar imediatamente
    await this.processEvent(fullEvent);
  }

  // ===== MÉTODOS DE CONFIGURAÇÃO =====

  updateConfig(newConfig: Partial<EventBusConfig>): void {
    this.config = { ...this.config, ...newConfig };
    console.log('⚙️ [EVENT-BUS] Configuração atualizada');
  }

  pause(): void {
    this.stopProcessing();
    console.log('⏸️ [EVENT-BUS] Processamento pausado');
  }

  resume(): void {
    this.startProcessing();
    console.log('▶️ [EVENT-BUS] Processamento retomado');
  }
}

// ===== INSTÂNCIA SINGLETON =====

export const financialEventBus = new FinancialEventBus({
      maxRetries: 3,
  retryDelay: 1000,
  deadLetterQueueEnabled: true,
  batchProcessing: true,
  batchSize: 10,
  batchTimeout: 5000,
  enableMetrics: true,
  enableAudit: true
});

// Registrar handlers automaticamente ao importar
let autoHandlersBootstrapped = false;
if (typeof window !== 'undefined') {
  // Apenas no cliente
  import('./eventHandlers').then(({ 
    registerAllEventHandlers, 
    TherapeuticAttendanceEventHandler,
  }) => {
    if (autoHandlersBootstrapped) return;
    autoHandlersBootstrapped = true;

    registerAllEventHandlers();
    // Registrar handler específico de pauta eletrônica
    // Fazer cast para EventHandler<FinancialEvent> para compatibilidade
    financialEventBus.subscribe(
      'therapeutic-attendance.closed',
      new TherapeuticAttendanceEventHandler() as any as EventHandler<FinancialEvent>
    );
    // billing.invoice.paid / billing.invoice.failed: tratados só no servidor (stripeWebhooks),
    // com IDs fixos por fatura. No navegador eles duplicavam receitas e cobranças (BUG-008).
  }).catch(err => {
    console.warn('⚠️ [EVENT-BUS] Erro ao registrar handlers:', err);
  });
}

// ===== EXPORTS =====

export default financialEventBus;
export { FinancialEventBus };
// Types já estão definidos acima